import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Clock,
  Columns2,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minimize2,
  Minus,
  Monitor,
  MoveHorizontal,
  PersonStanding,
  Plus,
  Scale,
  Sparkles,
  TrendingUp,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/buttock-augmentation-surgery-mumbai";

const BUTTOCK_FAQS = [
  {
    question: "Is Brazilian Butt Lift safe?",
    answer:
      "BBL safety depends entirely on technique. The complication associated with BBL fatalities is fat embolism, which occurs when fat is injected into or near the gluteal muscle and enters large blood vessels. Fat injected exclusively in the subcutaneous plane above the muscle does not carry this risk. Dr. Jain performs all BBL fat injection above the gluteal muscle in the subcutaneous plane, consistent with current international safety guidelines. Patients should ask any BBL surgeon directly about their injection technique before proceeding.",
  },
  {
    question: "How long do I need to avoid sitting after BBL?",
    answer:
      "Direct sitting on the buttocks is avoided for 2 weeks after BBL to prevent pressure on the transferred fat during the period when it is establishing its blood supply. A BBL cushion, which transfers the sitting pressure to the thighs rather than the buttocks, is used when sitting cannot be avoided. Normal sitting resumes gradually from week 3 onward. This protocol is not required after buttock implants.",
  },
  {
    question: "How much volume increase is possible with BBL?",
    answer:
      "This depends on how much donor fat is available. BBL produces moderate augmentation limited by the amount of fat that can be safely harvested from donor areas and the 60 to 80 percent survival rate of transferred fat. Patients wanting more significant volume increase than their donor fat allows are better served by implants or a combination approach.",
  },
  {
    question: "What percentage of transferred fat survives in BBL?",
    answer:
      "Approximately 60 to 80 percent of transferred fat cells survive permanently. The remaining 20 to 40 percent is resorbed by the body over the first 3 months. The injection volume at surgery accounts for this resorption so the final result at 3 months reflects the intended outcome.",
  },
  {
    question: "Will buttock augmentation results look natural?",
    answer:
      "Yes, when the augmentation is proportionate to the patient's body frame. BBL results feel natural because the material is the patient's own fat tissue. Implants produce a natural-looking contour when correctly sized for the patient's anatomy. Disproportionate augmentation, using too much volume relative to the patient's waist and hip dimensions, is what produces an unnatural appearance regardless of technique.",
  },
  {
    question: "Can buttock augmentation be combined with other procedures?",
    answer:
      "Yes. BBL is inherently combined with liposuction of the donor areas as part of the same procedure. Buttock augmentation is frequently combined with tummy tuck, thigh lift, and other body contouring procedures as part of a comprehensive lower body transformation. The combination is planned based on what the patient's health safely allows in a single surgery.",
  },
  {
    question: "How long do buttock augmentation results last?",
    answer:
      "Surviving BBL fat is permanently integrated and does not disappear. Significant weight changes after surgery affect the transferred fat as they affect fat everywhere else in the body: weight loss reduces it, weight gain increases it. Buttock implants are long-lasting with no scheduled replacement unless a complication occurs. Both approaches produce durable results in patients who maintain a stable weight.",
  },
  {
    question: "What are the risks of buttock augmentation?",
    answer:
      "For BBL, the primary risks are fat resorption producing less augmentation than planned, asymmetry requiring a second session, and fat embolism which is avoided by subcutaneous-only injection technique. For implants, risks include infection, implant displacement, seroma, and in rare cases implant removal. Both procedures carry standard surgical risks including bleeding and wound healing issues. Choosing a surgeon who performs BBL using subcutaneous-only injection technique is the most important safety decision in this procedure.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Buttock augmentation",
      alternateName: [
        "Brazilian Butt Lift",
        "Buttock Augmentation with Implants",
        "BBL",
        "Gluteal fat transfer",
        "Buttock implants",
      ],
      description:
        "Enhancement of buttock size, shape, and projection using autologous fat transfer (BBL), silicone gluteal implants, or combined techniques; often with liposuction of donor areas or waist for proportion.",
      procedureType: "surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Buttock" },
      howPerformed:
        "BBL: liposuction harvest, fat processing, subcutaneous-only injection above gluteal muscle. Implants: intramuscular gluteal pocket via intergluteal crease incision. Combination as indicated; general anesthesia; 2–5 hours.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "BBL injection plane", value: "Subcutaneous only (above gluteal muscle)" },
        { "@type": "PropertyValue", name: "Implant placement", value: "Intramuscular gluteus maximus" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: BUTTOCK_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function ButtockAugmentationJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Body", href: "/#services" },
    { name: "Buttock Augmentation", href: "/buttock-augmentation-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Body</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Buttock Augmentation Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Brazilian Butt Lift &amp; Buttock Implants by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Buttock Augmentation" icon="✨" size="hero"  imageSrc={HERO.buttockAugmentation} imageAlt="Buttock augmentation"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What is Buttock Augmentation Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Buttock augmentation enhances the size, shape, and projection of the buttocks to create a more curvaceous, lifted, and
              proportionate lower body profile. It is performed using fat transfer, silicone implants, or a combination of both
              depending on how much volume is needed and whether the patient has adequate donor fat for transfer.
            </p>
            <p>
              The goal in modern buttock augmentation is not maximum size. It is proportion: buttocks that are balanced with the waist,
              hips, and thighs, that project naturally in clothing and in profile, and that transition smoothly into the surrounding
              lower body contour. Disproportionately large results look operated on. Results that fit the patient&apos;s body frame look
              natural. Dr. Jain plans every procedure around the patient&apos;s existing body proportions rather than a generic size target.
            </p>
            <p>
              Dr. Vicky Jain performs buttock augmentation using the Brazilian Butt Lift fat transfer technique for patients
              with adequate donor fat, implants for patients with insufficient fat or who want more significant volume increase, and
              combination approaches when both volume and contour refinement are needed. The procedure takes 2 to 5 hours depending on
              technique and is frequently combined with liposuction of the waist and flanks to enhance the waist-to-hip ratio alongside
              the buttock enhancement.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Buttock Augmentation</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                For Brazilian Butt Lift, the quality of the fat injection technique directly determines the safety profile and the
                survival rate of transferred fat. Fat injected into the gluteal muscle rather than beneath the skin in the
                subcutaneous plane is the technique associated with the rare but serious complication of fat embolism. Dr. Jain
                performs all BBL fat injection in the subcutaneous plane above the gluteal muscle, which is the technique supported by
                current safety guidelines from international plastic surgery associations. This is a non-negotiable aspect of the
                procedure.
              </p>
              <p>
                For buttock implants, the implant is placed within the gluteus maximus muscle rather than beneath it. Intramuscular
                placement provides better coverage over the implant, reduces the risk of visible implant edges, and produces a more
                natural contour than submuscular placement. The incision is placed in the gluteal crease between the two buttocks where
                it is concealed by normal anatomy and not visible in clothing or swimwear.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61,37,102,0) 20%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatAddressesSection() {
  const cards = [
    {
      title: "Flat or Underprojected Buttocks",
      icon: Layers,
      copy: "Buttocks with insufficient forward projection that read as flat in profile regardless of lower body muscle development. Fat transfer or implants add the volume and projection that exercise alone cannot build when the underlying structure does not support it.",
    },
    {
      title: "Small Buttocks Relative to Body Frame",
      icon: Minimize2,
      copy: "Buttocks that are disproportionately small relative to the patient's waist, hips, and thighs, creating a lower body that reads as unbalanced. Augmentation brings the buttock size into proportion with the surrounding anatomy.",
    },
    {
      title: "Loss of Volume from Aging or Weight Loss",
      icon: Scale,
      copy: "Buttock volume reduces with age and significant weight loss as fat in the gluteal region decreases. Fat transfer restores this volume using fat harvested from areas where it is in excess, producing a natural result with a simultaneous body contouring benefit.",
    },
    {
      title: "Buttock Asymmetry",
      icon: Columns2,
      copy: "Unequal volume or projection between the two sides creates visible asymmetry in clothing and in profile. Fat transfer allows precise volume adjustment on each side independently to correct the imbalance.",
    },
    {
      title: "Poor Waist-to-Hip Ratio",
      icon: MoveHorizontal,
      copy: "The waist-to-hip ratio is the relationship between waist width and hip width. Augmenting the buttocks alongside liposuction of the waist and flanks enhances this ratio by increasing the hip dimension while reducing the waist simultaneously.",
    },
    {
      title: "Sagging Buttock Contour",
      icon: TrendingUp,
      copy: "Buttocks that have descended with age or weight loss, losing their upper pole fullness and projecting primarily in the lower pole. Fat grafting to the upper buttock restores the lifted, rounded contour of a younger buttock shape.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-addresses-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Indications</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What Buttock Augmentation Addresses</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PROCEDURE_TYPES = [
  {
    title: "Brazilian Butt Lift (Fat Transfer)",
    body:
      "The most commonly performed buttock augmentation technique worldwide. Fat is harvested by liposuction from the abdomen, flanks, lower back, or thighs, purified to isolate healthy fat cells, and injected in small passes into the subcutaneous tissue of the buttocks across multiple planes and areas to create a natural, smooth result. Approximately 60 to 80 percent of transferred fat survives permanently. Results are visible immediately and stabilize at 3 months as resorption is complete. Best for patients with adequate donor fat who want a natural feel and the simultaneous body contouring benefit of liposuction.",
  },
  {
    title: "Buttock Implants",
    body:
      "Silicone implants are placed within the gluteus maximus muscle through an incision in the gluteal crease. Implants provide a larger, more predictable volume increase than fat transfer and are appropriate for patients with insufficient donor fat for BBL or those who want more significant augmentation than fat alone can achieve. The implant feel is different from natural tissue but modern silicone buttock implants produce a natural-looking contour when correctly sized and positioned.",
  },
  {
    title: "Combination Approach",
    body:
      "Implants provide the primary volume increase while fat grafting refines the contour, fills in transitions between the implant and surrounding tissue, and adds projection in specific areas that the implant alone does not address. Used for patients who want significant volume increase alongside precise contour refinement.",
  },
];

function TypesSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white">Types of Buttock Augmentation Surgery</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {PROCEDURE_TYPES.map((item, index) => (
            <div
              key={item.title}
              className={`bg-white p-8 border border-[#E8E2D9] ${index === 2 ? "lg:col-span-2 lg:max-w-4xl lg:mx-auto lg:w-full" : ""}`}
            >
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BBLSafetySection() {
  return (
    <section
      className="bg-[#F5F2EC] py-10 md:py-14 border-y border-gold/30"
      data-testid="bbl-safety-section"
      aria-labelledby="bbl-safety-heading"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto border-l-4 border-gold pl-6 md:pl-8 py-2">
          <h2 id="bbl-safety-heading" className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">
            BBL Safety: What You Need to Know
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The Brazilian Butt Lift has historically been associated with a higher rate of serious complications than most cosmetic
              procedures, specifically fat embolism, a rare but potentially fatal complication that occurs when fat enters the
              bloodstream through the gluteal vasculature. The risk is technique-dependent. Fat injected into or beneath the gluteal
              muscle is in close proximity to large blood vessels. Fat injected exclusively in the subcutaneous plane above the muscle
              does not carry the same risk profile.
            </p>
            <p>
              Dr. Jain performs all BBL fat injection in the subcutaneous plane in strict adherence to the safety guidelines issued by
              the Multi-Society Task Force on Fat Injection and updated recommendations from international plastic surgery bodies.
              Patients considering BBL should ask any surgeon they consult with directly about their injection technique and
              whether they inject fat into the muscle. This is the single most important safety question in BBL surgery. Dr. Jain&apos;s
              answer is unambiguous: fat is injected only in the subcutaneous plane.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Buttock Augmentation Surgery is Performed</h2>
          </div>
          <div className="space-y-8 text-deep-navy/70 leading-relaxed">
            <div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Brazilian Butt Lift</h3>
              <p className="mb-4">
                Surgery takes 2 to 4 hours under general anesthesia. Dr. Jain marks the donor areas and the buttock injection zones
                before surgery with the patient standing upright. Liposuction is performed first to harvest fat from the planned donor
                areas: abdomen, flanks, lower back, and thighs depending on where adequate donor fat exists. The harvested fat is
                processed by centrifugation to separate viable fat cells from blood, fluid, and damaged cells.
              </p>
              <p>
                The purified fat is injected into the buttocks in small passes through multiple entry points using fine cannulas,
                distributing fat evenly throughout the subcutaneous tissue in the upper, central, and lower buttock. The injection is
                performed entirely above the gluteal muscle. Symmetry and projection are assessed during injection and adjustments are
                made in real time before closure. A compression garment is applied to the donor areas. The buttocks are not compressed.
              </p>
            </div>
            <div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Buttock Implants</h3>
              <p>
                Surgery takes 2 to 3 hours under general anesthesia. A single incision is made in the gluteal crease between the two
                buttocks. A pocket is created within the gluteus maximus muscle on each side and the implant is positioned within this
                pocket. The implant is centered within the muscle, assessed for symmetry, and the pocket is closed in layers before skin
                closure. The crease incision conceals the scar within the natural anatomy of the gluteal fold.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Week 1",
      description:
        "Swelling and discomfort in the donor areas and buttocks. Direct sitting on the buttocks is avoided for the first 2 weeks. A BBL cushion or pillow is used to sit on the thighs rather than the buttocks when sitting is necessary. Compression garment worn on donor areas.",
      icon: BedDouble,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Light daily activity resumes. Limited sitting protocol continues. The buttock shape is visible even through swelling. Donor areas look improved as initial swelling reduces.",
      icon: Monitor,
    },
    {
      period: "Weeks 3 to 4",
      description:
        "Return to desk work for most patients. Normal sitting gradually resumes from week 3 onward as the transplanted fat establishes its blood supply and becomes more tolerant of pressure.",
      icon: PersonStanding,
    },
    {
      period: "Month 3",
      description:
        "Fat resorption is complete. Surviving fat is permanently integrated. Final buttock shape and volume are assessed at this visit. Exercise including lower body training resumes from 6 to 8 weeks.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_ROWS = [
  {
    label: "Material",
    bbl: "Patient's own fat. No foreign material.",
    implant: "Silicone implant within gluteal muscle.",
  },
  {
    label: "Feel",
    bbl: "Natural. Identical to normal gluteal fat.",
    implant: "Firm. Natural-looking but different feel from native tissue.",
  },
  {
    label: "Volume Increase",
    bbl: "Moderate. Limited by available donor fat.",
    implant: "Significant. Not limited by donor fat availability.",
  },
  {
    label: "Additional Benefit",
    bbl: "Simultaneous liposuction of donor areas contours the waist and body.",
    implant: "No additional body contouring benefit.",
  },
  {
    label: "Fat Availability Requirement",
    bbl: "Yes. Patient must have adequate donor fat. Not appropriate for very thin patients.",
    implant: "No. Appropriate for patients of any body fat level.",
  },
  {
    label: "Result Stability",
    bbl: "60 to 80 percent of fat survives permanently. Final result at 3 months.",
    implant: "Stable immediately. No resorption.",
  },
  {
    label: "Best For",
    bbl: "Patients with adequate donor fat wanting natural feel and waist contouring alongside buttock enhancement.",
    implant: "Patients with minimal donor fat or wanting more significant volume than BBL achieves.",
  },
];

function BBLVsImplantsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="bbl-vs-implants-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding Your Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">
            Brazilian Butt Lift vs. Buttock Implants
          </h2>
        </div>
        <div className="max-w-6xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs sm:text-sm md:text-base min-w-[720px]">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-3 pr-2 sm:pr-4 font-serif text-premium-purple font-normal w-[20%]" />
                <th scope="col" className="py-3 px-2 sm:px-3 font-serif text-premium-purple bg-white/80">
                  BBL
                </th>
                <th scope="col" className="py-3 pl-2 sm:pl-3 font-serif text-premium-purple bg-white/80">
                  Buttock Implants
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-3 pr-2 sm:pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-3 px-2 sm:px-3 text-deep-navy/70 align-top">{row.bbl}</td>
                  <td className="py-3 pl-2 sm:pl-3 text-deep-navy/70 align-top">{row.implant}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Buttock Augmentation?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              For Brazilian Butt Lift, good candidates are adults with adequate donor fat in at least one area of the body, typically
              the abdomen, flanks, or thighs, who want moderate buttock enhancement with a natural result and are willing to follow the
              post-operative no-sitting protocol for 2 weeks. Very thin patients without sufficient donor fat are not appropriate BBL
              candidates and are better served by implants or a combination approach.
            </p>
            <p>
              For buttock implants, good candidates are patients who want significant buttock volume increase, have insufficient donor
              fat for BBL, or want a more predictable volume outcome than fat transfer provides. Both approaches require patients to be
              in good general health, at a stable weight, non-smokers or willing to stop before surgery, and to have realistic
              expectations about proportionate rather than maximum augmentation. Patients who are still losing weight should complete
              that process before buttock augmentation as weight loss after surgery affects the fat component of BBL results and changes
              how implants look in the surrounding tissue.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const PatientResultsSection = () => <PatientTestimonials procedureName="Buttock" />;

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Questions</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Frequently Asked Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {BUTTOCK_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`buttock-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Buttock Augmentation Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your body proportions, donor fat availability, and augmentation goals to
          determine whether BBL, implants, or a combination approach produces the right result for your anatomy. The safety protocol for
          fat injection technique is explained in detail. You leave with a clear surgical plan and a realistic understanding of the
          expected outcome and recovery.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Liposuction",
      description: "Waist and flank contouring to enhance the waist-to-hip ratio alongside buttock augmentation.",
      href: "/liposuction-surgery-mumbai",
    },
    {
      name: "Tummy Tuck",
      description: "Complete lower body transformation addressing the abdomen and buttocks together.",
      href: "/tummy-tuck-surgery-mumbai",
    },
    {
      name: "Thigh Lift",
      description: "Address thigh skin excess alongside buttock enhancement for complete lower body contouring.",
      href: "/thigh-lift-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Related Procedures</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Related Procedures</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ButtockAugmentationPage() {
  return (
    <>
      <SEO
        title="Buttock Augmentation | BBL & Implants by Dr. Vicky Jain"
        description="Flat, small, or asymmetric buttocks affect body proportion. Dr. Vicky Jain performs Brazilian Butt Lift and buttock implants. Book your consultation."
        canonical="/buttock-augmentation-surgery-mumbai"
      />
      <ButtockAugmentationJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhatAddressesSection />
      <TypesSection />
      <BBLSafetySection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <BBLVsImplantsSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
